import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import { EDITOR_VERSION, INVITE_BUTTON } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from 'utils/email';

import { UpgradeLock } from '../DownloadModal/OptionTitle';
import { InviteTeamMemberModal } from './InviteTeamMemberModal';

type InviteTeamMembersProps = {
  disabled?: boolean;
  hideLock?: boolean;
  onUpgrade?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onInvite?: (email: string, done?: () => void) => void;
};

export const InviteTeamMembers = ({
  disabled = false,
  hideLock = false,
  onUpgrade = () => {},
  onInvite = () => {},
}: InviteTeamMembersProps) => {
  const [value, setValue] = useState('');
  const { t } = useTranslation('common_navbar');
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteMemberModal, setShowInviteMemberModal] = useState(false);
  const showUpgradeLock = disabled && !hideLock;

  const handleChange = (event: FormEvent<HTMLInputElement>) => setValue(event.currentTarget.value);

  const handleSendInviteButtonClick = () => {
    if (value && validateEmail(value)) {
      // Mixpanel event
      Mixpanel.track(INVITE_BUTTON.eventName, { editor_version: EDITOR_VERSION, member_email: value });

      setIsInvalidEmail(false);
      setShowInviteMemberModal(true);
    } else {
      setIsInvalidEmail(true);
    }
  };

  const handleInviteModalClose = () => {
    setIsLoading(false);
    setShowInviteMemberModal(false);
  };

  const handleInviteTeamMemberClick = () => {
    setIsLoading(true);
    onInvite(value, () => {
      setIsLoading(false);
      setShowInviteMemberModal(false);
    });
  };

  const inviteTeamMembersTestId = 'invite-team-members';
  const upgradeLockTestId = 'invite-team-members-upgrade-lock';
  const sendInviteInputTestId = 'invite-team-members-send-invite-input';
  const sendInviteButtonTestId = 'invite-team-members-send-invite-button';

  return (
    <>
      <Flex flexDir='column' gap='2' data-testid={inviteTeamMembersTestId}>
        {showUpgradeLock && (
          <Flex
            flexDir='row'
            justifyContent='space-between'
            alignItems='center'
            ml='2'
            my='4'
            color='font.500'
            data-testid={upgradeLockTestId}
          >
            <UpgradeLock
              handleClickUpgrade={onUpgrade}
              upgradeText={t('modals.download.upgrade-business')}
              postText={t('modals.share-panel.invite-team-members.post-text')}
            />
          </Flex>
        )}

        <Text
          fontWeight={disabled ? 'medium' : 'semibold'}
          fontSize='sm'
          lineHeight='1.25'
          color={disabled ? 'font.300' : 'font.500'}
        >
          {t('modals.share-panel.invite-team-members.title')}
        </Text>

        <Flex gap='2'>
          <Box flexGrow={1}>
            <Input
              value={value}
              onChange={handleChange}
              placeholder='Enter an email address'
              size='sm'
              disabled={disabled}
              type='email'
              isInvalid={isInvalidEmail}
              errorBorderColor='red.300'
              data-testid={sendInviteInputTestId}
            />
          </Box>
          <Button
            onClick={handleSendInviteButtonClick}
            disabled={disabled}
            variant='action'
            size='sm'
            width='24'
            data-testid={sendInviteButtonTestId}
          >
            {t('modals.share-panel.send-invite')}
          </Button>
        </Flex>
      </Flex>
      <InviteTeamMemberModal
        isOpen={showInviteMemberModal}
        onClick={handleInviteTeamMemberClick}
        onClose={handleInviteModalClose}
        isLoading={isLoading}
        loadingText={t('modals.share-panel.invite-team-members.loading-text')}
      />
    </>
  );
};
